import { Component, Injector, OnInit, Type } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  EntityParsed,
  FunnelInfo, OfferExtras,
  PaymentAvailableMethods,
  PaymentMethodEnum,
  PaymentMethodType,
  PaymentPaypalConfiguration,
  PaymentSIAConfiguration,
  PaymentSIAItem,
  StepData
} from 'models';
import { combineLatest, Observable } from 'rxjs';
import { debounceTime, filter, first, map } from 'rxjs/operators';
import {EnvironmentService, PaypalService} from 'services';
import {ModalButtonPositionEnum, ModalType} from '../../../../../../app-shared/models/modal.interface';
import { PaymentMethodTypes } from '../../../constants/payment.constants';
import { ProcessStepBaseComponent } from '../../_core/process-step-base.component';

@Component({
  selector: 'sb-step-payment-method',
  templateUrl: './payment-method.component.html',
  styleUrls: ['./payment-method.component.scss']
})
export class PaymentMethodComponent extends ProcessStepBaseComponent implements OnInit {
  public availablePaymentTypes: PaymentMethodType[] = [];
  public paymentMethodsEnum = PaymentMethodEnum;
  private paymentTypes = PaymentMethodTypes;
  protected paypalService: PaypalService;
  public paymentSIAConfigurationData: PaymentSIAItem[];
  public paymentSIAConfigurationUrl: string;
  public siaFormSubmitReady = false;
  public mgmCode: string;
  public couponCode: string;
  public funnelHasCoupon: boolean;
  public isSmartTopUpMandatory: boolean;
  public textNote$: Observable<string>;
  public errorMessage: string;
  public translationKey: string;
  public isInviteCodeEnabled: boolean;
  public isCouponEnabled: boolean;
  public offerExtras: OfferExtras;
  public isSmartTopUpActive: OfferExtras;
  public payByLinkValidityHours: number;

  constructor(injector: Injector) {
    super(injector);
    this.paypalService = this.injectServiceInstance(PaypalService as Type<PaypalService>);
    this.translateService = this.injectServiceInstance(TranslateService as Type<TranslateService>);
  }

  ngOnInit() {
    // super.shouldShowBackButton();
    super.isStickyNavigation();
    super.setFormGroup(this.formsService.generateStepPaymentMethodForm());
    super.setFormStoreDataFunction(this.appStoreFacadeService.step.storeStepPaymentMethodValue);
    this.getFormDataAndValidation();
    this.textNote$ = this.appStoreFacadeService.route.queryParams$
      .pipe(filter(Boolean), map((queryParams: any) => queryParams.textNote));
    this.formGroup.get('paymentMethod.method').valueChanges
      .subscribe(id => {
        if (id) {
          this.sendCustomEvent(id);
        }
      });
  }

  public submitSiaForm() {
    this.siaFormSubmitReady = true;
  }

  public inferPaymentButtonText(value: PaymentMethodEnum) {
    switch (value) {
      case PaymentMethodEnum.CreditCard:
        return 'STEP.ACTIONS.GO-NEXT-CC';
      case PaymentMethodEnum.NoVSC:
        return 'STEP.ACTIONS.PAY_NOVSC';
      case PaymentMethodEnum.PayByLink:
        return 'STEP.ACTIONS.PAY-BY-LINK';
      default:
        return 'STEP.ACTIONS.PAY';
    }
  }

  get isPaymentMethodInvalid() {
    const paymentMethodGroup = this.formGroup.get('paymentMethod');
    return paymentMethodGroup.touched && paymentMethodGroup.invalid;
  }

  isPaymentMethodDisabled(id: string) {
    return id === PaymentMethodEnum.NoVSC && this.offerExtras.smartTopUp && !this.isSmartTopUpMandatory;
  }

  getPaymentTranslationKey() {
    let translationKey = '';
    if (this.isSmartTopUpMandatory) {
      const selectedPaymentMethod = this.formGroup.get('paymentMethod').get('method').value;
      switch (selectedPaymentMethod) {
        case PaymentMethodEnum.CreditCard:
          translationKey = 'STEP.PAYMENT-METHOD.CREDIT-CARD.SMART_TOP_UP_DESCRIPTION';
          break;
        // case PaymentMethodEnum.PayPal:
        //  translationKey = 'STEP.PAYMENT-METHOD.PAY-BY-LINK.SMART_TOP_UP_DESCRIPTION';
        //  break;
        case PaymentMethodEnum.PayPal:
          translationKey = 'STEP.PAYMENT-METHOD.PAYPAL.SMART_TOP_UP_DESCRIPTION';
          break;
      }
    }
    return translationKey;
  }

  private getFormDataAndValidation() {
    this.subscriptions.add(
      combineLatest([
        this.appStoreFacadeService.entity.paymentAvailableMethods$,
        this.appStoreFacadeService.entity.paymentPaypalConfiguration$,
        this.appStoreFacadeService.entity.paymentSIAConfiguration$,
        this.appStoreFacadeService.entity.funnelInfo$,
        this.appStoreFacadeService.entity.offerExtras$
      ])
        .pipe(
          filter((args) => args.every(Boolean))
        )
        .subscribe(([{ methods }, paypalConfigurationEntity, paymentData, funnelInfo, offerExtras]: [
          PaymentAvailableMethods, PaymentPaypalConfiguration, PaymentSIAConfiguration, FunnelInfo, EntityParsed]) => {
          this.isSmartTopUpActive = offerExtras.value.smartTopUp;
          if (methods.includes(PaymentMethodEnum.PayPal)) {
            if (offerExtras.value.smartTopUp) {
              // in caso di refresh o ritorno su questo step da SIA, rimodifico la url del SDK di paypal se necessario
              const ppurl = document.getElementById('ppurl');
              if (ppurl.getAttribute('src').includes('authorize')) {
                const paypalConfiguration = this.environmentService.getPaypalConfiguration();
                ppurl.setAttribute('src', `https://www.paypal.com/sdk/js?client-id=${paypalConfiguration.clientId}&currency=EUR&locale=it_IT&intent=tokenize&vault=true`);
              }
              this.paypalService.createPaypalSTUButtons(paypalConfigurationEntity, '.sb-paypal-container');
            } else {
              this.paypalService.createPaypalButtons(paypalConfigurationEntity, '.sb-paypal-container');
            }
          }

          this.availablePaymentTypes = this.paymentTypes.filter(type => {
            return methods.includes(type.id as PaymentMethodEnum);
          });

          if (methods.includes(PaymentMethodEnum.CreditCard)) {
            const { paymentItems } = paymentData;
            this.paymentSIAConfigurationData = paymentItems;
            this.paymentSIAConfigurationUrl = paymentData.redirectUrl;
          }

          this.offerExtras = (offerExtras.value as unknown as OfferExtras);
          this.isSmartTopUpMandatory = funnelInfo.isSmartTopUpMandatory;
          this.payByLinkValidityHours = funnelInfo.payByLinkValidityHours;
          // Set default
          if (!this.formGroup.value.paymentMethod.method) {
            const firstAvailablePaymentType =
              this.availablePaymentTypes.filter(method => !this.isPaymentMethodDisabled(method.id)).shift();
            this.formGroup.patchValue({ paymentMethod: { method: firstAvailablePaymentType.id } });
          }
          this.funnelHasCoupon = funnelInfo.hasCoupon ? funnelInfo.hasCoupon : false;
          this.isInviteCodeEnabled = funnelInfo.isInviteCodeEnabled ? funnelInfo.isInviteCodeEnabled : false;
          this.isCouponEnabled = funnelInfo.isCouponEnabled ? funnelInfo.isCouponEnabled : false;
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.step.paymentMethod$
        .subscribe((stepData: StepData) => {
          super.setFormGroupStepData(stepData);
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.step.paymentMethodValue$
        .pipe(debounceTime(150), first(Boolean))
        .subscribe(() => {
          this.sendAnalyticsData();
        })
    );
    this.subscriptions.add(
      this.appStoreFacadeService.route.queryParams$.subscribe(params => {
        if (params && params.textNote) {
          this.inferErrorMessage(params && params.textNote);
        }
      })
    );
  }

  public onChangePaymentMethodValue = () => {
    // We use this instead of a subscription because we want it to happen only when the user ACTUALLY presses the button
    this.errorMessage = '';
    this.translationKey = '';
  }

  private inferErrorMessage = textNote => {
    const textNoteParts = (textNote || '').split('|');
    if (textNoteParts.length === 1) {
      this.errorMessage = textNote;
    } else {
      this.translationKey = textNote.split('|')[0];
      this.errorMessage = textNote.split('|')[1] || '';
      // Wait for next cycle
      setTimeout(() => {
        if (this.translationKey) {
          if (this.translationKey === this.translateService.instant(this.translationKey)) {
            this.translationKey = '';
          } else {
            this.errorMessage = '';
          }
        }
      }, 0);
    }
  }

  goToNextStep() {
    const selectedPaymentMethod = this.formGroup.get('paymentMethod.method').value as unknown as PaymentMethodEnum;
    if (selectedPaymentMethod === PaymentMethodEnum.NoVSC) {
      const message = this.translateService.instant(`STEP.PAYMENT-METHOD.NOVSC.MODAL-MESSAGE`);
      const title = this.translateService.instant(`STEP.PAYMENT-METHOD.NOVSC.MODAL-TITLE`);
      this.analyticsService.sendCustomPage('/popup/pagare-successivamente');
      return this.openConfirmationModal(message, title);
    } else if (selectedPaymentMethod === PaymentMethodEnum.PayByLink) {
      const txt = (this.payByLinkValidityHours && this.payByLinkValidityHours === 1) ?
        'STEP.PAYMENT-METHOD.PAY-BY-LINK.MODAL-MESSAGE-ONE' : 'STEP.PAYMENT-METHOD.PAY-BY-LINK.MODAL-MESSAGE';
      const message = this.translateService.instant(txt,
        {hours: (this.payByLinkValidityHours ? this.payByLinkValidityHours : 24)});
      const title = this.translateService.instant(`STEP.PAYMENT-METHOD.PAY-BY-LINK.MODAL-TITLE`);
      this.analyticsService.sendCustomPage('/popup/pay-by-link');
      return this.openConfirmationModal(message, title);
    }
    return super.goToNextStep();
  }

  private openConfirmationModal(message, title) {
    this.appStoreFacadeService.modals.openModal(
      ModalType.Confirmation,
      {
        closeButton: true,
        buttonsPosition: ModalButtonPositionEnum.VERTICAL_REVERSE,
        buttonContainerClass: 'wide',
        additionalClasses: 'extra-padding wide',
        options: {
          confirmTranslationKey: 'MODALS.CONFIRM.ACTION.CONFIRM'
        },
        hideCancelButton: true,
        message,
        title,
        resolve: (confirm) => {
          if (confirm) {
            return super.goToNextStep();
          }
        }
      }
    );
    return;
  }

  private sendCustomEvent(paymentmethod) {
    this.analyticsService.sendCustomEvent({
      custom_category: 'ecommerce',
      custom_action: 'selezione metodo pagamento',
      custom_label: paymentmethod
    });
  }

}

